import { PrototypeApiError } from "../apiClient";
import { byId, inputValue, textareaValue } from "../domUtils";
import { handleApiError } from "../authGuards";
import type { PlanCode, PrototypeRuntime, Root } from "../prototypeTypes";
import { collectIdeaFormPayload, collectAiFormPayload, hasUploadingAttachments } from "./ideaAndAiFormActions";
import { isValidUrl } from "../validation";
import { hasCompletedSourceInput, SOURCE_INPUT_REQUIRED_ERROR } from '../../../domain/sourceAttachmentPolicy';
import { launchSideConfetti } from "../celebration";
import { focusFirstFieldError, showFieldError, validateIdeaSubtitle, validateSubtitle, validateChapterCount, validateOptionalLongText } from "../wizardValidation";
import { resetAiCreateTurnstile, resetAiCreateTurnstileAfterError, resetIdeaTurnstile, resetIdeaTurnstileAfterError } from "../turnstileWidgetController";
import { getRecoverableTurnstileToken } from '../turnstileRuntime';
import { resetIdeaWizardForm, resetAiCreateWizardForm } from "../formProfileController";
import { aiChapterDefaultPreset } from '../../../domain/aiChapterTargetPolicy';
import { siteSettingsStore } from "../siteSettingsStore";
import {
  beginLongFormSubmission,
  captureAiDraft,
  captureIdeaDraft,
  clearLongFormSubmissionDraft,
  isLatestLongFormSubmission,
  ownsLongFormUi,
  sameDraft,
  submissionKeyForDraft,
} from './longFormDraftLifecycle';

export async function submitIdea(
  root: Root,
  runtime: PrototypeRuntime,
  handlers: {
    closeModal(root: Root, id: string): void;
    openModal(root: Root, id: string): void;
    failValidation(message: string): false;
    notifyPreservedDraftSuccess?(): void;
    recordRuntimeError(runtime: PrototypeRuntime, error: unknown): void;
    currentPlan: PlanCode;
    isLoggedIn: boolean;
  },
) {
  const title = inputValue(root, "ideaTitle").trim();
  const subtitle = inputValue(root, "ideaSubtitle").trim();
  // Track whether any field has an error so we can show a single friendly
  // toast instead of repeating field-specific messages.
  let hasError = false;
  if (title.length < 8 || title.length > 120) {
    showFieldError(root, "ideaTitle", title.length === 0 ? "Please enter a topic title with at least 8 characters." : title.length > 120 ? "Topic title must not exceed 120 characters." : "Topic title must be at least 8 characters.");
    hasError = true;
  }
  const subtitleError = validateIdeaSubtitle(subtitle);
  if (subtitleError) {
    showFieldError(root, "ideaSubtitle", subtitleError);
    hasError = true;
  }
  const linkError = validateSourceInputs(root, 'ideaLinkList', 'ideaAttachmentList');
  if (linkError) {
    showFieldError(root, "ideaLinkList", linkError);
    hasError = true;
  }
  // Block submission if attachments are still uploading.
  if (hasUploadingAttachments(root, "ideaAttachmentList")) {
    showFieldError(root, "ideaAttachmentList", "Attachments are still uploading. Please wait for them to finish.");
    hasError = true;
  }
  // Validate optional step-3 fields: strengths / weaknesses (≥20 chars if filled)
  const strengths = textareaValue(root, "ideaStrengths").trim();
  const strengthsError = validateOptionalLongText(strengths, "Expected Strengths");
  if (strengthsError) {
    showFieldError(root, "ideaStrengths", strengthsError);
    hasError = true;
  }
  const weaknesses = textareaValue(root, "ideaWeaknesses").trim();
  const weaknessesError = validateOptionalLongText(weaknesses, "Areas to Go Deeper");
  if (weaknessesError) {
    showFieldError(root, "ideaWeaknesses", weaknessesError);
    hasError = true;
  }
  if (hasError) {
	focusFirstFieldError(root, ['ideaTitle', 'ideaSubtitle', 'ideaLinkList', 'ideaAttachmentList', 'ideaStrengths', 'ideaWeaknesses']);
    return handlers.failValidation("Please review the highlighted fields and try again.");
  }

  const payload = collectIdeaFormPayload(root);
  const submissionHost = byId(root, 'submitIdeaModal');
  const snapshot = captureIdeaDraft(root);
  const fingerprint = { title, subtitle, strengths, weaknesses, description: '', links: payload.links, attachments: payload.attachments };
  const submissionId = submissionHost ? submissionKeyForDraft(submissionHost, 'submissionId', fingerprint) : crypto.randomUUID();
  const submission = submissionHost ? beginLongFormSubmission(submissionHost, snapshot) : null;

  try {
    // Use the token from the visible Turnstile widget that was solved in Step 3.
    // If the interactive provider is available, it has a cached token from the
    // user's manual challenge solve. Fall back to the invisible provider for
    // backward compatibility (dev mode without a site key).
    let turnstileToken: string;
    if (runtime.interactiveTurnstile?.hasToken()) {
      turnstileToken = await runtime.interactiveTurnstile.getToken("community-idea-submit");
    } else {
      turnstileToken = await getRecoverableTurnstileToken(runtime, "community-idea-submit");
    }
    await runtime.api.submitCommunityIdea({ submissionId, title, subtitle: subtitle || undefined, description: '', strengths: strengths || undefined, weaknesses: weaknesses || undefined, links: payload.links, attachments: payload.attachments, turnstileToken });
    const settlesDraft = Boolean(submissionHost && submission && isLatestLongFormSubmission(submissionHost, submission.sequence) && sameDraft(captureIdeaDraft(root), snapshot));
    const ownsUi = Boolean(submissionHost && submission && ownsLongFormUi(submissionHost, submission.attempt));
    if (settlesDraft) {
      resetIdeaWizardForm(root);
      clearLongFormSubmissionDraft(submissionHost);
    }
    if (settlesDraft && ownsUi) {
	  resetIdeaTurnstile(root);
      handlers.closeModal(root, "submitIdeaModal");
      handlers.openModal(root, "ideaSuccessModal");
      launchSideConfetti();
    } else if (ownsUi) {
      resetIdeaTurnstileAfterError(root, runtime);
      handlers.notifyPreservedDraftSuccess?.();
    }
    return true;
  } catch (error) {
    const ownsUi = submissionHost && submission ? ownsLongFormUi(submissionHost, submission.attempt) : true;
    if (!ownsUi) return false;
    if (error instanceof PrototypeApiError && (error.status === 401 || error.status === 403)) {
      // Check for turnstile-specific errors: reset the visible widget so the
      // user gets a fresh challenge, and re-lock the submit button.
      const body = (error.body ?? {}) as Record<string, unknown>;
      const code = typeof body.error === "string" ? body.error : "";
      if (code === "turnstile_required" || code === "turnstile_failed") {
        resetIdeaTurnstileAfterError(root, runtime);
        handlers.failValidation("Security verification failed. Please complete the check and try again.");
        return;
      }
      handleApiError(runtime, handlers.currentPlan, handlers.isLoggedIn, error, { root, intent: "idea" });
      return false;
    } else if (error instanceof PrototypeApiError && error.status === 400) {
      const fields = (error.body as any)?.fields;
      handlers.failValidation(`Invalid input: ${fields?.join(", ") || "Please check your input"}`);
      return false;
    } else if (error instanceof PrototypeApiError && error.status === 503) {
      const body = (error.body ?? {}) as Record<string, unknown>;
      const code = typeof body.error === "string" ? body.error : "";
      if (code === "turnstile_secret_not_configured") {
        handlers.failValidation("Security verification is not configured in this environment.");
        return false;
      }
      handlers.failValidation("Security verification is temporarily unavailable. Please try again in a moment.");
      return false;
    }
    handlers.recordRuntimeError(runtime, error);
    handlers.failValidation("Idea submission failed. Please try again.");
    return false;
  } finally {
    if (submissionHost && submission && ownsLongFormUi(submissionHost, submission.attempt)) delete submissionHost.dataset.submitting;
  }
}

export async function submitAiTopic(
  root: Root,
  runtime: PrototypeRuntime,
  currentPlan: PlanCode,
  handlers: {
    closeModal(root: Root, id: string): void;
    openModal(root: Root, id: string): void;
    failValidation(message: string): false;
    notifyPreservedDraftSuccess?(): void;
    recordRuntimeError(runtime: PrototypeRuntime, error: unknown): void;
    isLoggedIn: boolean;
  },
) {
  const submissionHost = root.querySelector<HTMLElement>('#aiCreateModal');
  if (submissionHost?.dataset.submitting === 'true') return false;
  const title = inputValue(root, "aiTopicTitle").trim();
  const subtitle = inputValue(root, "aiTopicSubtitle").trim();
  const strengths = textareaValue(root, "aiTopicStrengths").trim();
  const weaknesses = textareaValue(root, "aiTopicWeaknesses").trim();
  // User target (exact/range/automatic); blank input becomes the balance-aware
  // SaaS default before the API boundary. The server still caps stale values.
  const chaptersRaw = inputValue(root, "aiTopicChapters").trim();
  const cachedAvailable = Number(root.querySelector<HTMLElement>('#aiCreatedList')?.dataset.quotaAvailable);
  if (!Number.isSafeInteger(cachedAvailable) || cachedAvailable < 0) {
		return handlers.failValidation('AI Credits are still loading. Please wait a moment and try again.');
	}
  const availableCredits = cachedAvailable;

  // Validate all fields, collecting all errors before showing a single
  // friendly toast (instead of returning on the first error).
  let hasError = false;
  if (title.length < 8 || title.length > 120) {
    showFieldError(root, "aiTopicTitle", title.length === 0 ? "Please enter a topic title with at least 8 characters." : title.length > 120 ? "Topic title must not exceed 120 characters." : "Topic title must be at least 8 characters.");
    hasError = true;
  }
  const subtitleError = validateSubtitle(subtitle);
  if (subtitleError) {
    showFieldError(root, "aiTopicSubtitle", subtitleError);
    hasError = true;
  }
  const sourceError = validateAiSources(root);
  if (sourceError) {
    showFieldError(root, "aiLinkList", sourceError);
    hasError = true;
  }
  // Block submission if attachments are still uploading.
  if (hasUploadingAttachments(root, "aiAttachmentList")) {
    showFieldError(root, "aiAttachmentList", "Attachments are still uploading. Please wait for them to finish.");
    hasError = true;
  }
  // Validate optional step-3 fields: chapters (integer or x-y), strengths/weaknesses (≥20 chars if filled)
  const chaptersError = validateChapterCount(chaptersRaw);
  if (chaptersError) {
    showFieldError(root, "aiTopicChapters", chaptersError);
    hasError = true;
  }
  const strengthsError = validateOptionalLongText(strengths, "Expected Strengths");
  if (strengthsError) {
    showFieldError(root, "aiTopicStrengths", strengthsError);
    hasError = true;
  }
  const weaknessesError = validateOptionalLongText(weaknesses, "What to Spend Less Time On");
  if (weaknessesError) {
    showFieldError(root, "aiTopicWeaknesses", weaknessesError);
    hasError = true;
  }
  if (hasError) {
	focusFirstFieldError(root, ['aiTopicTitle', 'aiTopicSubtitle', 'aiLinkList', 'aiAttachmentList', 'aiTopicChapters', 'aiTopicStrengths', 'aiTopicWeaknesses']);
    return handlers.failValidation("Please review the highlighted fields and try again.");
  }

  const payload = collectAiFormPayload(root);
  // The hidden carrier always holds a selected preset; fall back to the
  // default chain only if it was ever left empty.
  const requestedChapters = chaptersRaw || aiChapterDefaultPreset(availableCredits);
  const snapshot = captureAiDraft(root);
  const fingerprint = {
    plan: currentPlan, requestedChapters, title, subtitle, strengths, weaknesses, description: '',
    references: payload.links.map((link) => link.url), links: payload.links, attachments: payload.attachments,
  };
  const idempotencyKey = submissionHost ? submissionKeyForDraft(submissionHost, 'idempotencyKey', fingerprint) : crypto.randomUUID();
  const submission = submissionHost ? beginLongFormSubmission(submissionHost, snapshot) : null;
  const submitButton = root.querySelector<HTMLButtonElement>('#aiSubmitBtn');
  if (submitButton) {
    submitButton.disabled = true;
    submitButton.setAttribute('aria-busy', 'true');
  }

  try {
    // Use the token from the visible Turnstile widget that was solved in Step 3.
    // If the interactive provider is available, it has a cached token from the
    // user's manual challenge solve. Fall back to the invisible provider for
    // backward compatibility (dev mode without a site key).
    let turnstileToken: string;
    if (runtime.interactiveTurnstile?.hasToken()) {
      turnstileToken = await runtime.interactiveTurnstile.getToken("ai-create");
    } else {
      turnstileToken = await getRecoverableTurnstileToken(runtime, "ai-create");
    }
    await runtime.api.createAiJob({
      plan: currentPlan,
      requestedChapters,
      title,
      description: '',
      strengths: strengths || undefined,
      weaknesses: weaknesses || undefined,
      subtitle: subtitle || undefined,
      references: payload.links.map((l) => l.url),
      links: payload.links,
      attachments: payload.attachments,
      turnstileToken,
    }, idempotencyKey);
    const settlesDraft = Boolean(submissionHost && submission && isLatestLongFormSubmission(submissionHost, submission.sequence) && sameDraft(captureAiDraft(root), snapshot));
    const ownsUi = Boolean(submissionHost && submission && ownsLongFormUi(submissionHost, submission.attempt));
    if (settlesDraft) {
      resetAiCreateWizardForm(root, { resetSubmitButton: ownsUi });
      clearLongFormSubmissionDraft(submissionHost);
    }
    if (settlesDraft && ownsUi) {
	  resetAiCreateTurnstile(root);
      handlers.closeModal(root, "aiCreateModal");
      handlers.openModal(root, "aiSuccessModal");
      launchSideConfetti();
    } else if (ownsUi) {
      resetAiCreateTurnstileAfterError(root, runtime);
      handlers.notifyPreservedDraftSuccess?.();
    }
  } catch (error) {
    const ownsUi = submissionHost && submission ? ownsLongFormUi(submissionHost, submission.attempt) : true;
    if (!ownsUi) return;
    // A verified token is single-use. Keep the same submission key for an
    // unknown-result retry, but always require a fresh human verification.
    resetAiCreateTurnstileAfterError(root, runtime);
    if (error instanceof PrototypeApiError && (error.status === 401 || error.status === 403)) {
      // Check for turnstile-specific errors: reset the visible widget so the
      // user gets a fresh challenge, and re-lock the submit button.
      const body = (error.body ?? {}) as Record<string, unknown>;
      const code = typeof body.error === "string" ? body.error : "";
      if (code === "turnstile_required" || code === "turnstile_failed") {
        handlers.failValidation("Security verification failed. Please complete the check and try again.");
        return;
      }
      handleApiError(runtime, currentPlan, handlers.isLoggedIn, error, { root, intent: "ai" });
      return;
    } else if (error instanceof PrototypeApiError && error.status === 409) {
		const code = typeof (error.body as Record<string, unknown> | null)?.error === 'string'
			? String((error.body as Record<string, unknown>).error)
			: '';
		if (code === 'active_task_limit_reached') {
			const limit = siteSettingsStore.get().maxConcurrentAiCreateTasks;
			handlers.failValidation(`You already have ${limit} active AI Create tasks. Wait for one to finish before creating another.`);
			return;
		}
		if (code === 'idempotency_conflict') {
			handlers.failValidation('This submission may already exist. Check AI Create History before trying a different request.');
			return;
		}
		handlers.failValidation('This AI Create request could not be accepted. Check your Credits and active tasks, then try again.');
      return;
    } else if (error instanceof PrototypeApiError && error.status === 400) {
      const fields = (error.body as any)?.fields;
      handlers.failValidation(`Invalid input: ${fields?.join(", ") || "Please check your input"}`);
      return;
    } else if (error instanceof PrototypeApiError && error.status === 503) {
      const body = (error.body ?? {}) as Record<string, unknown>;
      const code = typeof body.error === "string" ? body.error : "";
      if (code === "turnstile_secret_not_configured") {
        handlers.failValidation("AI Create is waiting on security verification configuration in this environment.");
        return;
      }
      if (code === "queue_not_configured") {
        handlers.failValidation("AI generation queue is not configured in this environment.");
        return;
      }
      if (code === "ai_provider_not_configured") {
        handlers.failValidation("AI generation provider is not configured in this environment.");
        return;
      }
      handlers.failValidation("AI Create service is temporarily unavailable. Please try again later.");
      return;
    }
    handlers.recordRuntimeError(runtime, error);
    handlers.failValidation("AI topic generation failed. Please try again.");
  } finally {
    if (submissionHost && submission && ownsLongFormUi(submissionHost, submission.attempt)) {
      delete submissionHost.dataset.submitting;
      if (submitButton) submitButton.removeAttribute('aria-busy');
    }
  }
}

export function validateReferenceLinks(root: Root, listId: string, requireOne: boolean): string | null {
  const list = byId(root, listId);
  if (!list) return requireOne ? "Please add at least one reference link." : null;
  let validCount = 0;
  const rows = list.querySelectorAll<HTMLElement>(".idea-link-row");
  for (const row of rows) {
    const url = (row.querySelector<HTMLInputElement>(".idea-link-url")?.value ?? "").trim();
    const label = (row.querySelector<HTMLInputElement>(".idea-link-desc")?.value ?? "").trim();
		if (!url && !label) continue;
		if (!isValidUrl(url)) return "Reference links must be valid https URLs.";
		if (label.length > 0 && (label.length < 2 || label.length > 64)) return "Reference link descriptions must be 2 to 64 characters if provided.";
		validCount += 1;
  }
  if (requireOne && validCount === 0) return "Please add at least one reference link.";
  return null;
}

/**
 * AI Create accepts either kind of source the API already supports. Link rows
 * are still individually validated, while a file-only submission is valid.
 */
export function validateAiSources(root: Root): string | null {
  return validateSourceInputs(root, 'aiLinkList', 'aiAttachmentList');
}

export function validateSourceInputs(root: Root, linkListId: string, attachmentListId: string): string | null {
  const linkError = validateReferenceLinks(root, linkListId, false);
  if (linkError) return linkError;
  const payload = linkListId === 'ideaLinkList' ? collectIdeaFormPayload(root) : collectAiFormPayload(root);
  return hasCompletedSourceInput(payload.links, payload.attachments) ? null : SOURCE_INPUT_REQUIRED_ERROR;
}
